import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Link,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import { formatDistanceToNow } from 'date-fns';
import type { Activity, ActivityType } from '@/hooks';
import { dateFnsLocale } from '@/utils/locale';
import { useAuthStore } from '@/stores/authStore';

/** Message i18n key per activity type (dashboard namespace, activity.*). */
const activityMessageKeys: Record<ActivityType, string> = {
  booking_created: 'activity.bookingCreated',
  work_started: 'activity.workStarted',
  work_completed: 'activity.workCompleted',
  approval_requested: 'activity.approvalRequested',
  approval_received: 'activity.approvalReceived',
  parts_ordered: 'activity.partsOrdered',
  customer_arrived: 'activity.customerArrived',
};

const activityIcons: Record<ActivityType, React.ReactNode> = {
  booking_created: <DirectionsCarIcon sx={{ color: 'primary.light' }} />,
  work_started: <BuildIcon sx={{ color: 'warning.dark' }} />,
  work_completed: <CheckCircleIcon sx={{ color: 'success.main' }} />,
  approval_requested: <ThumbUpIcon sx={{ color: 'secondary.light' }} />,
  approval_received: <ThumbUpIcon sx={{ color: 'success.main' }} />,
  parts_ordered: <WarningIcon sx={{ color: 'warning.main' }} />,
  customer_arrived: <PersonIcon sx={{ color: 'primary.light' }} />,
};

interface RecentActivityFeedProps {
  data?: Activity[];
}

export function RecentActivityFeed({ data: activities = [] }: RecentActivityFeedProps) {
  const { t } = useTranslation('dashboard');
  const canViewTeam = useAuthStore((s) => s.hasPermission('hr.view_team'));

  const dateLocale = dateFnsLocale();
  const formatTime = (time: string) => {
    return formatDistanceToNow(new Date(time), { addSuffix: true, locale: dateLocale });
  };

  const activityMessage = (activity: Activity): string =>
    t(activityMessageKeys[activity.type], {
      service: activity.meta?.serviceType ?? '',
      customer: activity.meta?.customerName ?? '',
    });

  /** Reg number, work order number and actor, linked where a target exists (#3728, #3937). */
  const activityDetails = (activity: Activity): React.ReactNode[] => {
    const meta = activity.meta;
    const details: React.ReactNode[] = [];
    if (meta?.vehicleRegNumber) {
      details.push(
        meta.vehicleId ? (
          <Link component={RouterLink} to={`/vehicles/${meta.vehicleId}`} underline="hover">
            {meta.vehicleRegNumber}
          </Link>
        ) : (
          meta.vehicleRegNumber
        ),
      );
    }
    if (meta?.workOrderNumber) {
      details.push(
        <Link component={RouterLink} to={`/workorders/${activity.id}`} underline="hover">
          {meta.workOrderNumber}
        </Link>,
      );
    }
    if (meta?.actorName) {
      // The HR page is gated by hr.view_team; without it the name stays text.
      details.push(
        meta.actorId && canViewTeam ? (
          <>
            {t('activity.byLabel')}{' '}
            <Link
              component={RouterLink}
              to={`/hr?member=${encodeURIComponent(meta.actorId)}`}
              underline="hover"
            >
              {meta.actorName}
            </Link>
          </>
        ) : (
          t('activity.by', { name: meta.actorName })
        ),
      );
    }
    details.push(formatTime(activity.time));
    return details;
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="h6" gutterBottom>
            {t('recentActivity')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('lastHours', { hours: 3 })}
          </Typography>
        </Box>

        <Divider />

        <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
          {activities.map((activity, index) => (
            <Box key={activity.id}>
              <ListItem sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {activityIcons[activity.type]}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2">{activityMessage(activity)}</Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {activityDetails(activity).map((detail, i) => (
                        <Fragment key={i}>
                          {i > 0 && ' · '}
                          {detail}
                        </Fragment>
                      ))}
                    </Typography>
                  }
                />
              </ListItem>
              {index < activities.length - 1 && <Divider component="li" />}
            </Box>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}
